import React, { useState } from "react";
import {
  Badge,
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
} from "../../components/micro";
import { Form, Formik } from "formik";
import Breadcrumb from "../../components/common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import {
  createCommunalGarden,
  editCommunalGarden,
} from "../../redux/communalgarden/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().nullable().required("Name is required"),
  thoughts: Yup.string().trim().nullable().required("Thought is required"),
});

const AddEditCommunalGarden = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const communalGarden = location?.state?.garden;
  const isEditMode = location?.state?.mode;
  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/communal-garden");

  const handleConfirm = () => {
    setIsLoading(true);
    const payload = {
      creatorName: formValues.name,
      thoughts: formValues.thoughts,
    };

    const action = communalGarden?._id
      ? editCommunalGarden({ _id: communalGarden._id, ...payload })
      : createCommunalGarden(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "createCommunalGarden/fulfilled" ||
          res.type === "editCommunalGarden/fulfilled"
        ) {
          navigate("/communal-garden");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  // update status
  const updateStatus = (element) => {
    dispatch(
      editCommunalGarden({
        _id: element?._id,
        approvalStatus: element?.approvalStatus,
      })
    ).then((res) => {
      if (res.type === "editCommunalGarden/fulfilled") {
        navigate("/communal-garden");
      }
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode === "edit"
            ? "Edit Communal Garden"
            : "Create Communal Garden"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbParent2={"Communal Garden"}
          breadCrumbActive={
            isEditMode === "edit"
              ? "Edit Communal Garden"
              : "Create Communal Garden"
          }
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      <div className="bg-white shadow-md rounded-lg p-6">
        <Formik
          initialValues={{
            name: communalGarden?.creatorName || "",
            thoughts: communalGarden?.thoughts || "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            setFieldValue,
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
          }) => (
            <Form className="">
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div className="w-full">
                  <InputWithLabel
                    idFromName="name"
                    name="name"
                    label="Creator Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errorsText={errors.name}
                    errors={errors.name && touched.name}
                    className={`${
                      errors?.name && touched?.name
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                </div>
                <div></div>
                <div className="">
                  <InputWithLabel
                    idFromName="thoughts"
                    label="Thoughts*"
                    type="textarea"
                    rows={1}
                    onChange={(e) => {
                      setFieldValue("thoughts", e.target.value);
                    }}
                    onBlur={handleBlur}
                    value={values.thoughts}
                    errorsText={errors.thoughts}
                    errors={errors.thoughts && touched.thoughts}
                    className={`${
                      errors?.thoughts && touched?.thoughts
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                </div>
              </div>
              {/* approval buttons */}
              {isEditMode === "edit" && (
                <div className="w-28 ">
                  <div className="flex items-center gap-4">
                    <p>Status: </p>
                    <Badge
                      className={`uppercase ${
                        communalGarden?.approvalStatus === "approve"
                          ? "bg-themeBtn text-white"
                          : communalGarden?.approvalStatus === "pending"
                          ? "border-lightyellow text-lightredtheme bg-lightyellow border"
                          : "text-white bg-red"
                      }`}
                      value={
                        communalGarden?.approvalStatus === "approve"
                          ? "Approved"
                          : communalGarden?.approvalStatus === "pending"
                          ? "Pending"
                          : "Rejected"
                      }
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-3">
                <div className="flex items-center gap-4 ">
                  {communalGarden?.approvalStatus === "pending" && (
                    <>
                      <div className="flex justify-start py-2">
                        <button
                          className={`text-white flex items-center justify-center px-6 py-2.5 rounded uppercase transition 
                          ${
                            isEditMode === "edit" && !isEditing
                              ? "bg-themeBtn/50 cursor-not-allowed"
                              : "bg-themeBtn "
                          }`}
                          type="button"
                          onClick={() =>
                            updateStatus({
                              ...communalGarden,
                              approvalStatus: "approve",
                            })
                          }
                          disabled={isEditMode === "edit" && !isEditing}
                        >
                          Approve
                        </button>
                      </div>
                      <div className="flex justify-start py-2">
                        <button
                          className={`text-white flex items-center justify-center px-6 py-2.5 rounded uppercase ${
                            isEditMode === "edit" && !isEditing
                              ? "bg-red/50 cursor-not-allowed"
                              : "bg-red"
                          }`}
                          type="button"
                          onClick={() =>
                            updateStatus({
                              ...communalGarden,
                              approvalStatus: "reject",
                            })
                          }
                          disabled={isEditMode === "edit" && !isEditing}
                        >
                          Reject
                        </button>
                      </div>
                    </>
                  )}
                  {communalGarden?.approvalStatus === "approve" && (
                    <div className="flex justify-start py-2">
                      <button
                        className={`text-white bg-red flex items-center justify-center px-6 py-2.5 rounded uppercase
                           ${
                             isEditMode === "edit" && !isEditing
                               ? "bg-red/50 cursor-not-allowed"
                               : "bg-red"
                           }
                          `}
                        type="button"
                        onClick={() =>
                          updateStatus({
                            ...communalGarden,
                            approvalStatus: "reject",
                          })
                        }
                        disabled={isEditMode === "edit" && !isEditing}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {communalGarden?.approvalStatus === "reject" && (
                    <div className="flex justify-start py-2">
                      <button
                        className={`text-white flex items-center justify-center px-6 py-2.5 rounded uppercase transition 
                        ${
                          isEditMode === "edit" && !isEditing
                            ? "bg-themeBtn/50 cursor-not-allowed"
                            : "bg-themeBtn"
                        }`}
                        type="button"
                        onClick={() =>
                          updateStatus({
                            ...communalGarden,
                            approvalStatus: "approve",
                          })
                        }
                        disabled={isEditMode === "edit" && !isEditing}
                      >
                        Approve
                      </button>
                    </div>
                  )}
                </div>
                <DefaultPrimaryButton
                  type="button"
                  value={isEditMode === "edit" ? "Back" : "Cancel"}
                  onClick={handleCancelClick}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton
                        type="submit"
                        value="Save"
                        onClick={(e) => {}}
                      />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <>
                    <DefaultButton type="submit" value="confirm" />
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm()}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default AddEditCommunalGarden;
